import { useState, useCallback, useEffect } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest, graphRequest } from './msalConfig';
import { ReactComponent as Logo } from './assets/defineholylogo.svg';
import crossIcon from './assets/definholycross.svg';
import RecipeBrowser from './RecipeBrowser';
import RecipeDetail from './RecipeDetail';
import GroceryList from './GroceryList';
import DinnerOrder from './DinnerOrder';
import PostItBoard from './PostItBoard';
import DocumentVault from './DocumentVault';
import Fitness from './fitness/Fitness';
import Finance from './finance/Finance';
import Food from './food/Food';
import Centcom from './centcom/Centcom';
import FamilyVault from './familyvault/FamilyVault';
import Watch from './watch/Watch';
import Contacts from './contacts/Contacts';
import Insurance from './insurance/Insurance';
import { UserProfileProvider, useUserProfile } from './UserProfileContext';
import { LiturgicalProvider, useLiturgical } from './LiturgicalContext';
import { SEASON_OPTIONS } from './utils/liturgical';
import OneNoteTest from './pages/OneNoteTest';
import OneNoteSearchTest from './pages/OneNoteSearchTest';
import NotesDestination from './pages/NotesDestination';
import SearchDestination from './pages/SearchDestination';
import { userHasNotesAccess } from './config/notesAccess';
import { userCanViewContacts } from './config/ownerAccess';
import { userHasFinanceAccess } from './config/financeAccess';
import { userHasInsuranceAccess } from './config/insuranceAccess';
import './App.css';

// Primary tabs for the mobile bottom bar only — this array is not what
// renders the desktop nav, which is a hardcoded <ul> further down.
const PRIMARY_TABS = [
  { key: 'finance', label: 'Finance', icon: '$', financeGated: true }, // Marc and Angela only
  { key: 'fitness', label: 'Fitness', icon: '\u2660' },      // ♠ (placeholder)
  { key: 'food', label: 'Food', icon: '\u2666' },            // ♦ (placeholder)
];

// Takes the Finance slot for anyone without finance access (the kids).
const CENTCOM_TAB = { key: 'centcom', label: 'CENTCOM', icon: '\u2302' }; // ⌂

// Secondary modules in "More" menu
const MORE_MODULES = [
  { key: 'watch', label: 'Watch' },
  { key: 'contacts', label: 'Contacts', contactsGated: true },   // owners + read-only viewers
  { key: 'vault', label: 'Vault' },
  { key: 'grocery', label: 'Shopping List' },
  { key: 'dinner', label: 'Dinner Plan' },
  { key: 'postit', label: 'Post-It Board' },
  { key: 'recipes', label: 'Recipes' },
  { key: 'theology', label: 'Theology' },
  { key: 'file-drop', label: 'File Drop' },
  { key: 'familyvault', label: 'Media' },
  { key: 'notes', label: 'Notes', notesGated: true },
  { key: 'finance', label: 'Finance', financeGated: true },      // Marc and Angela only
  { key: 'insurance', label: 'Insurance', insuranceGated: true }, // Marc and Angela only
];

function AppInner() {
  const { instance, accounts } = useMsal();
  const isLoggedIn = useIsAuthenticated();
  // Default to centcom when logged in, home when not
  const [page, setPage] = useState('home');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const { nickname, showPrompt, updateNickname, prefs } = useUserProfile();
  const darkMode = prefs.darkMode || false;
  // Liturgical season drives the app chrome color; accent flags single-day
  // observances (martyr reds, feast golds). Both are null if romcal can't
  // resolve the day, which leaves the default indigo theme in place.
  const { season, accent } = useLiturgical();
  const [swimResumeCandidate, setSwimResumeCandidate] = useState(null);

  const displayName = nickname || accounts[0]?.name || '';

  // When user logs in, go to centcom
  const activePage = isLoggedIn && page === 'home' ? 'centcom' : page;

  // Sync outer page state from URL on mount — supports deep-links into /familyvault/*
  useEffect(() => {
    if (window.location.pathname.startsWith('/familyvault')) {
      setPage('familyvault');
    } else if (window.location.pathname === '/onenote-test') {
      setPage('onenote-test');
    } else if (window.location.pathname === '/notes') {
      setPage('notes');
    } else if (window.location.pathname === '/onenote-search-test') {
      setPage('onenote-search-test');
    } else if (window.location.pathname === '/search') {
      setPage('search');
    } else if (window.location.pathname === '/insurance') {
      setPage('insurance');
    }
  }, []);

  function navigate(key) {
    setPage(key);
    setShowMore(false);
    if (key === 'familyvault' && !window.location.pathname.startsWith('/familyvault')) {
      window.history.pushState(null, '', '/familyvault');
    } else if (key === 'notes' && window.location.pathname !== '/notes') {
      window.history.pushState(null, '', '/notes');
    } else if (key === 'search' && window.location.pathname !== '/search') {
      window.history.pushState(null, '', '/search');
    } else if (key === 'insurance' && window.location.pathname !== '/insurance') {
      window.history.pushState(null, '', '/insurance');
    } else if (key !== 'familyvault' && key !== 'notes' && key !== 'search' && key !== 'insurance' &&
               (window.location.pathname.startsWith('/familyvault') ||
                window.location.pathname === '/onenote-test' ||
                window.location.pathname === '/onenote-search-test' ||
                window.location.pathname === '/notes' ||
                window.location.pathname === '/search' ||
                window.location.pathname === '/insurance')) {
      window.history.pushState(null, '', '/');
    }
  }

  function handleLogin() {
    instance.loginPopup(loginRequest).catch(console.error);
  }

  function handleLogout() {
    instance.logoutPopup().then(() => {
      setPage('home');
    }).catch(console.error);
  }

  function openRecipe(recipeId) {
    setSelectedRecipe(recipeId);
    setPage('recipe-detail');
  }

  // Re-check for a saved swim session every time the user lands on CENTCOM
  useEffect(() => {
    if (!isLoggedIn) return;
    const raw = localStorage.getItem('dh_swimWorkoutInProgress');
    if (!raw) { setSwimResumeCandidate(null); return; }
    try {
      const saved = JSON.parse(raw);
      if (Date.now() - saved.savedAt < 3 * 60 * 60 * 1000) {
        setSwimResumeCandidate(saved);
      } else {
        localStorage.removeItem('dh_swimWorkoutInProgress');
        setSwimResumeCandidate(null);
      }
    } catch {
      localStorage.removeItem('dh_swimWorkoutInProgress');
      setSwimResumeCandidate(null);
    }
  }, [activePage, isLoggedIn]);

  function handleBannerResume() {
    try { localStorage.setItem('dh_swimAutoResume', '1'); } catch (e) { console.warn('localStorage write failed:', e); }
    setSwimResumeCandidate(null);
    navigate('fitness');
  }

  function handleBannerDismiss() {
    localStorage.removeItem('dh_swimWorkoutInProgress');
    setSwimResumeCandidate(null);
  }

  const isPrivate = ['centcom', 'fitness', 'vault', 'grocery', 'dinner', 'postit', 'recipes', 'recipe-detail', 'notes', 'watch', 'contacts'].includes(activePage);

  return (
    <div className={`App${isLoggedIn && activePage !== 'familyvault' ? ' App--with-bottombar' : ''}`} data-season={season || undefined} data-accent={accent || undefined} data-theme={darkMode ? 'dark' : undefined}>
      {/* TOP NAV BAR */}
      <nav className="App-nav">
        <div className="App-nav-left">
          <img
            src={crossIcon}
            className="App-nav-icon"
            alt="Define Holy"
            onClick={() => navigate(isLoggedIn ? 'centcom' : 'home')}
            style={{ cursor: 'pointer' }}
          />
          {/* Desktop nav links */}
          <ul className="App-nav-links App-nav-links--desktop">
            {!isLoggedIn && (
              <>
                <li><a href="#" onClick={e => { e.preventDefault(); navigate('home'); }}>Home</a></li>
                <li><a href="#" onClick={e => { e.preventDefault(); navigate('theology'); }}>Theology</a></li>
                <li><a href="#" onClick={e => { e.preventDefault(); navigate('file-drop'); }}>File Drop</a></li>
              </>
            )}
            {isLoggedIn && (
              <>
                <li><a href="#" className={activePage === 'centcom' ? 'App-nav-active' : ''} onClick={e => { e.preventDefault(); navigate('centcom'); }}>CENTCOM</a></li>
                <li><a href="#" className={activePage === 'watch' ? 'App-nav-active' : ''} onClick={e => { e.preventDefault(); navigate('watch'); }}>Watch</a></li>
                {userCanViewContacts(accounts[0]) && (
                  <li><a href="#" className={activePage === 'contacts' ? 'App-nav-active' : ''} onClick={e => { e.preventDefault(); navigate('contacts'); }}>Contacts</a></li>
                )}
                <li><a href="#" className={activePage === 'fitness' ? 'App-nav-active' : ''} onClick={e => { e.preventDefault(); navigate('fitness'); }}>Fitness</a></li>
                <li><a href="#" className={activePage === 'food' ? 'App-nav-active' : ''} onClick={e => { e.preventDefault(); navigate('food'); }}>Food</a></li>
                <li><a href="#" className={activePage === 'vault' ? 'App-nav-active' : ''} onClick={e => { e.preventDefault(); navigate('vault'); }}>Vault</a></li>
                <li><a href="#" className={activePage === 'grocery' ? 'App-nav-active' : ''} onClick={e => { e.preventDefault(); navigate('grocery'); }}>Shopping</a></li>
                <li><a href="#" className={activePage === 'dinner' ? 'App-nav-active' : ''} onClick={e => { e.preventDefault(); navigate('dinner'); }}>Dinner</a></li>
                <li><a href="#" className={activePage === 'postit' ? 'App-nav-active' : ''} onClick={e => { e.preventDefault(); navigate('postit'); }}>Post-It</a></li>
                <li><a href="#" className={activePage === 'recipes' ? 'App-nav-active' : ''} onClick={e => { e.preventDefault(); navigate('recipes'); }}>Recipes</a></li>
                <li><a href="#" className={activePage === 'familyvault' ? 'App-nav-active' : ''} onClick={e => { e.preventDefault(); navigate('familyvault'); }}>Media</a></li>
                {userHasFinanceAccess(accounts[0]) && (
                  <li><a href="#" className={activePage === 'finance' ? 'App-nav-active' : ''} onClick={e => { e.preventDefault(); navigate('finance'); }}>Finance</a></li>
                )}
                {userHasInsuranceAccess(accounts[0]) && (
                  <li><a href="#" className={activePage === 'insurance' ? 'App-nav-active' : ''} onClick={e => { e.preventDefault(); navigate('insurance'); }}>Insurance</a></li>
                )}
                {userHasNotesAccess(accounts[0]) && (
                  <li><a href="#" className={activePage === 'notes' ? 'App-nav-active' : ''} onClick={e => { e.preventDefault(); navigate('notes'); }}>Notes</a></li>
                )}
              </>
            )}
          </ul>
        </div>
        <div className="App-nav-right">
          {isLoggedIn && userHasNotesAccess(accounts[0]) && (
            <button
              className={`App-nav-search-btn${activePage === 'search' ? ' App-nav-active' : ''}`}
              onClick={() => navigate('search')}
              title="Search"
              aria-label="Search"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </button>
          )}
          {isLoggedIn && (
            <span
              className="App-nav-user App-nav-user--clickable"
              onClick={() => setShowProfileEditor(true)}
              title="Edit nickname"
            >
              {displayName}
            </span>
          )}
          <button
            className="App-login-btn"
            onClick={isLoggedIn ? handleLogout : handleLogin}
          >
            {isLoggedIn ? 'Logout' : 'Family Login'}
          </button>
        </div>
      </nav>

      {/* MOBILE BOTTOM TAB BAR (logged in only, hidden when FamilyVault owns the bottom bar) */}
      {isLoggedIn && activePage !== 'familyvault' && (
        <nav className="App-bottombar">
          {PRIMARY_TABS.map(tab => (tab.financeGated && !userHasFinanceAccess(accounts[0]) ? CENTCOM_TAB : tab)).map(tab => (
            <button
              key={tab.key}
              className={`App-bottombar-tab${activePage === tab.key ? ' App-bottombar-tab--active' : ''}`}
              onClick={() => navigate(tab.key)}
            >
              <span className="App-bottombar-icon">{tab.icon}</span>
              <span className="App-bottombar-label">{tab.label}</span>
            </button>
          ))}
          <button
            className={`App-bottombar-tab${showMore ? ' App-bottombar-tab--active' : ''}`}
            onClick={() => setShowMore(!showMore)}
          >
            <span className="App-bottombar-icon">{'\u2261'}</span>
            <span className="App-bottombar-label">More</span>
          </button>
        </nav>
      )}

      {/* More menu overlay (mobile) */}
      {showMore && (
        <div className="App-more-overlay" onClick={() => setShowMore(false)}>
          <div className="App-more-menu" onClick={e => e.stopPropagation()}>
            {MORE_MODULES.map(mod => {
              if (mod.notesGated && !userHasNotesAccess(accounts[0])) return null;
              if (mod.contactsGated && !userCanViewContacts(accounts[0])) return null;
              if (mod.financeGated && !userHasFinanceAccess(accounts[0])) return null;
              if (mod.insuranceGated && !userHasInsuranceAccess(accounts[0])) return null;
              return (
                <button
                  key={mod.key}
                  className={`App-more-item${activePage === mod.key ? ' App-more-item--active' : ''}`}
                  onClick={() => navigate(mod.key)}
                >
                  {mod.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Nickname prompt on first login */}
      {isLoggedIn && showPrompt && <NicknamePrompt onSave={updateNickname} fallbackName={accounts[0]?.name || ''} />}

      {/* Profile editor modal */}
      {showProfileEditor && (
        <ProfileEditor
          currentNickname={nickname}
          onSave={async (n) => { await updateNickname(n); setShowProfileEditor(false); }}
          onClose={() => setShowProfileEditor(false)}
        />
      )}

      {/* PAGE CONTENT */}
      {activePage === 'home' && !isLoggedIn && (
        <main className="App-landing">
          <Logo className="App-logo" aria-label="Define Holy" />
          <BuildStamp />
        </main>
      )}

      {activePage === 'centcom' && isLoggedIn && (
        <main className="App-page">
          {swimResumeCandidate && (
            <div className="App-swim-resume-banner">
              <div className="App-swim-resume-banner__info">
                <strong>{swimResumeCandidate.workout.templateName}</strong>
                <span>
                  Set {swimResumeCandidate.currentIndex + 1} of {swimResumeCandidate.workout.sets.length}
                  {swimResumeCandidate.completedSets.length > 0 &&
                    ` · ${swimResumeCandidate.completedSets.length} set${swimResumeCandidate.completedSets.length !== 1 ? 's' : ''} done`}
                </span>
              </div>
              <div className="App-swim-resume-banner__actions">
                <button className="App-swim-resume-btn" onClick={handleBannerResume}>Resume Workout</button>
                <button className="App-swim-resume-btn App-swim-resume-btn--dismiss" onClick={handleBannerDismiss}>Dismiss</button>
              </div>
            </div>
          )}
          <Centcom onOpenInsurance={userHasInsuranceAccess(accounts[0]) ? () => navigate('insurance') : null} />
        </main>
      )}

      {activePage === 'fitness' && isLoggedIn && (
        <main className="App-page">
          <Fitness />
        </main>
      )}

      {activePage === 'food' && isLoggedIn && (
        <main className="App-page">
          <Food />
        </main>
      )}

      {activePage === 'vault' && isLoggedIn && (
        <main className="App-page">
          <DocumentVault />
        </main>
      )}

      {activePage === 'familyvault' && isLoggedIn && (
        <main className="App-page">
          <FamilyVault />
        </main>
      )}

      {activePage === 'grocery' && isLoggedIn && (
        <main className="App-page">
          <GroceryList />
        </main>
      )}

      {activePage === 'dinner' && isLoggedIn && (
        <main className="App-page">
          <DinnerOrder />
        </main>
      )}

      {activePage === 'postit' && isLoggedIn && (
        <main className="App-page">
          <PostItBoard />
        </main>
      )}

      {activePage === 'recipes' && isLoggedIn && (
        <main className="App-page">
          <RecipeBrowser onSelect={openRecipe} />
        </main>
      )}

      {activePage === 'recipe-detail' && isLoggedIn && selectedRecipe && (
        <main className="App-page">
          <RecipeDetail recipeId={selectedRecipe} onBack={() => navigate('recipes')} />
        </main>
      )}

      {activePage === 'onenote-test' && isLoggedIn && (
        <main className="App-page">
          <OneNoteTest />
        </main>
      )}

      {activePage === 'notes' && isLoggedIn && (
        <main className="App-page">
          <NotesDestination />
        </main>
      )}

      {activePage === 'onenote-search-test' && isLoggedIn && (
        <main className="App-page">
          <OneNoteSearchTest />
        </main>
      )}

      {activePage === 'search' && isLoggedIn && (
        <main className="App-page">
          <SearchDestination />
        </main>
      )}

      {activePage === 'theology' && (
        <main className="App-page">
          <p style={{ textAlign: 'center', color: '#999' }}>Theology — coming soon</p>
        </main>
      )}

      {activePage === 'file-drop' && (
        <main className="App-page">
          <p style={{ textAlign: 'center', color: '#999' }}>File Drop — coming soon</p>
        </main>
      )}

      {activePage === 'finance' && isLoggedIn && userHasFinanceAccess(accounts[0]) && (
        <main className="App-page">
          <Finance />
        </main>
      )}

      {activePage === 'insurance' && isLoggedIn && userHasInsuranceAccess(accounts[0]) && (
        <main className="App-page ins-page">
          <Insurance />
        </main>
      )}

      {activePage === 'watch' && isLoggedIn && (
        <main className="App-page">
          <Watch />
        </main>
      )}

      {activePage === 'contacts' && isLoggedIn && userCanViewContacts(accounts[0]) && (
        <main className="App-page">
          <Contacts />
        </main>
      )}
    </div>
  );
}

// Small gray "build abc1234 · Oct 28, 6:15 PM" line on the landing page.
// Values are baked in by the build script (package.json); blank on `npm start`.
function BuildStamp() {
  const commit = process.env.REACT_APP_COMMIT;
  const builtAt = process.env.REACT_APP_BUILT_AT;
  if (!commit) return null;
  let when = '';
  if (builtAt) {
    when = new Date(builtAt).toLocaleString('en-US', {
      timeZone: 'America/Chicago', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  }
  return <p className="App-buildstamp">build {commit}{when ? ` · ${when}` : ''}</p>;
}

function NicknamePrompt({ onSave, fallbackName }) {
  const [value, setValue] = useState(fallbackName);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!value.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(value.trim());
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="Profile-overlay">
      <div className="Profile-modal">
        <h2>Welcome! Set Your Nickname</h2>
        <p>This is how you'll appear across the app.</p>
        {error && <p className="Fitness-error">{error}</p>}
        <form onSubmit={handleSubmit}>
          <input
            className="Profile-input"
            type="text"
            placeholder="Enter a nickname"
            value={value}
            onChange={e => setValue(e.target.value)}
            autoFocus
            required
          />
          <button className="Profile-save-btn" type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </form>
      </div>
    </div>
  );
}

function ProfileEditor({ currentNickname, onSave, onClose }) {
  // Display prefs write through immediately on change (same as the readings
  // collapse toggle), so they sit outside the nickname form rather than
  // waiting on its submit.
  const { prefs, updatePref, profileId } = useUserProfile();
  const [value, setValue] = useState(currentNickname);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!value.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(value.trim());
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="Profile-overlay" onClick={onClose}>
      <div className="Profile-modal" onClick={e => e.stopPropagation()}>
        <h2>Profile</h2>
        {error && <p className="Fitness-error">{error}</p>}
        <form onSubmit={handleSubmit}>
          <input
            className="Profile-input"
            type="text"
            placeholder="Nickname"
            value={value}
            onChange={e => setValue(e.target.value)}
            autoFocus
            required
          />
          <div className="Profile-actions">
            <button className="Profile-save-btn" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button className="Profile-cancel-btn" type="button" onClick={onClose}>Cancel</button>
          </div>
        </form>

        <div className="Profile-section">
          <label className="Profile-label" htmlFor="season-override">Liturgical theme</label>
          <select
            id="season-override"
            className="Profile-select"
            value={prefs.themeOverride || ''}
            onChange={e => updatePref('themeOverride', e.target.value || null)}
          >
            {SEASON_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <label className="Profile-check">
            <input
              type="checkbox"
              checked={prefs.darkMode || false}
              onChange={e => updatePref('darkMode', e.target.checked)}
            />
            <span>Dark mode</span>
          </label>

          {!profileId && (
            <p className="Profile-hint">
              Save a nickname first — display settings persist once your profile exists.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function App() {
  const { instance, accounts } = useMsal();
  const isLoggedIn = useIsAuthenticated();

  const getToken = useCallback(async () => {
    const account = accounts[0];
    if (!account) throw new Error('Not signed in');
    const response = await instance.acquireTokenSilent({ ...graphRequest, account });
    return response.accessToken;
  }, [instance, accounts]);

  const userEmail = accounts[0]?.username || '';

  return (
    <UserProfileProvider getToken={isLoggedIn ? getToken : null} userEmail={userEmail}>
      <LiturgicalProvider>
        <AppInner />
      </LiturgicalProvider>
    </UserProfileProvider>
  );
}

export default App;
